export const GUIDES = [
  {
    slug: "what-does-a-pickleball-agent-do",
    seoTitle: "What Does a Pickleball Agent Do? | SMG Sports Management",
    title: "What Does a Pickleball Agent Do? A Complete Guide for Players",
    description:
      "A pickleball agent explains the job: contracts, sponsorships, brand building and career planning — and how to know when you're ready for representation.",
    date: "2026-10-15",
    minutes: 4,
    body: [
      {
        t: "p",
        x: "Professional pickleball has grown faster than almost any sport in America. Prize money, tours and sponsorships have multiplied — and with them, the business decisions a player has to make. This guide explains what a pickleball agent actually does, so you can judge when representation is worth it.",
      },
      { t: "h2", x: "The short answer" },
      {
        t: "p",
        x: "A pickleball agent represents your interests off the court. You train and compete; the agent works the business around you — sponsorships, appearances, contracts, media and the long game of your career. The goal is simple: you play, the system around you runs properly.",
      },
      { t: "h2", x: "The five core jobs" },
      { t: "p", x: "At an agency like SMG Sports Management Group International, the work breaks down into five coordinated roles:" },
      {
        t: "ul",
        items: [
          "Sponsorships and endorsements — finding, negotiating and activating brand deals that fit your profile.",
          "Contract guidance — helping you understand terms and commitments before you sign anything.",
          "Brand development — positioning, identity, social strategy, content and audience growth.",
          "Media and appearances — interviews, events and public opportunities that raise your profile.",
          "Career planning — the long-term route from emerging pro to established professional, and beyond competition.",
        ],
      },
      { t: "h2", x: "Agency versus going alone" },
      {
        t: "p",
        x: "Early on, most players handle everything themselves, and honestly, it can work — until the volume arrives. When offers, media requests and deadlines start competing with training time, something slips. Usually it's the deals. Sometimes it's the sport.",
      },
      {
        t: "p",
        x: "An agent's value is coordination and leverage: one team that knows your value, keeps the calendar clean, and negotiates every opportunity as part of a career plan instead of as one-off transactions.",
      },
      { t: "h2", x: "When is the right time to look for representation?" },
      {
        t: "ul",
        items: [
          "You're winning or placing at your level and moving up a tour.",
          "Brands or tournaments have started contacting you directly.",
          "Results and content are growing, but you're unsure how to price or package them.",
          "The business side is eating into training time.",
        ],
      },
      {
        t: "p",
        x: "You don't need to be a household name. Agencies work with emerging pros and youth and NIL talent too — the earlier the foundation is built, the better the career compounds.",
      },
      { t: "h2", x: "What to look for in a pickleball agent" },
      {
        t: "p",
        x: "Ask who the agency already represents — a public roster is a good sign. Ask what services are actually included, how deals are sourced and negotiated, and who does the work day to day. Representation should feel like a system, not a business card.",
      },
      {
        t: "p",
        x: "SMG Sports Management Group International represents professional, emerging and youth pickleball players from Carlsbad, California. If you're ready to be considered, the athlete application takes a few minutes — the link is below.",
      },
    ],
  },
  {
    slug: "how-to-get-signed-in-pickleball",
    seoTitle: "How to Get Signed in Pickleball | SMG Sports Management",
    title: "How to Get Signed in Pickleball: A Player's Roadmap",
    description:
      "How to get signed in pickleball: what agencies look for, how to build your case as a player, and how the SMG athlete application works.",
    date: "2026-10-15",
    minutes: 4,
    body: [
      {
        t: "p",
        x: "Every week, players ask the same question: how do I get signed in pickleball? The honest answer is that you get signed by making your case impossible to ignore — results, trajectory and professionalism. Here is the roadmap agencies actually use to evaluate players.",
      },
      { t: "h2", x: "What getting signed means in pickleball" },
      {
        t: "p",
        x: "Unlike team sports with a draft, pickleball representation is won on the open market. Agencies look at competition results, growth trajectory, audience and character — then decide who is ready to be invested in. That's good news: you control most of the inputs.",
      },
      { t: "h2", x: "Step 1 — build a results record" },
      {
        t: "p",
        x: "Compete consistently on recognized tours and qualifiers — the APP Tour, the PPA circuits and the regional scenes that feed them. Results at your current level matter more than occasional flashes at a higher level. Agencies invest in trajectories, not one-off weekends.",
      },
      { t: "h2", x: "Step 2 — make your case easy to verify" },
      {
        t: "ul",
        items: [
          "A current ranking or results page link.",
          "Two or three full match clips — not just highlight reels.",
          "A short bio: age, location, level, playing style, goals.",
          "Social handles that show who you are on and off the court.",
        ],
      },
      { t: "h2", x: "Step 3 — apply the professional way" },
      {
        t: "p",
        x: "When you submit to an agency, you're already auditioning. Fill every field properly, write a bio that reads like a professional, and make the first impression a clean one. It takes ten extra minutes and it shows before anyone ever sees you play.",
      },
      { t: "h2", x: "What agencies look for beyond results" },
      {
        t: "ul",
        items: [
          "Growth curve — where you were twelve months ago versus today.",
          "Coachability — how you talk about losses, training and teammates.",
          "Audience — even a small, engaged following signals brand value.",
          "Character — for youth and NIL athletes especially, the family team around the player matters.",
        ],
      },
      { t: "h2", x: "How the SMG application works" },
      {
        t: "p",
        x: "SMG's athlete application asks for your name, contact details, level, age range, ranking, location and a short bio. Every submission is reviewed by the team, and players who fit current roster plans are contacted for a conversation. Youth inquiries require a parent or legal guardian.",
      },
      {
        t: "p",
        x: "The fastest way to get signed in pickleball is to start the conversation while you're building the record — not after. The application link is below.",
      },
    ],
  },
  {
    slug: "how-top-pickleball-players-build-their-brands",
    seoTitle: "How Top Pickleball Players Build Brands | SMG Sports Management",
    title: "How Top Pickleball Players Turn Results Into Brands",
    description:
      "What separates top pickleball players on the business side: positioning, content, partnerships — and the agency system that ties it all together.",
    date: "2026-10-15",
    minutes: 4,
    body: [
      {
        t: "p",
        x: "The top pickleball players in the world are no longer just athletes — they're brands. Tournaments, sponsorships and audiences have grown together, and the players who win off the court treat that side of the game as seriously as drilling. Here's how the business side actually works.",
      },
      { t: "h2", x: "Results open the door — positioning keeps it open" },
      {
        t: "p",
        x: "Medals create attention, but attention is temporary. The players who convert results into lasting value have a clear identity: a style of play people recognize, a story people follow, and a voice that stays consistent across tours, social channels and sponsorships.",
      },
      { t: "h2", x: "The brand-building stack" },
      {
        t: "ul",
        items: [
          "Identity — how you're described in one sentence: your game, your story, your promise.",
          "Content — regular, recognizable output that grows an audience between tournaments.",
          "Social strategy — platforms chosen deliberately, not everywhere at once.",
          "Partnerships — sponsorships chosen for fit, not just money; one wrong brand deal costs more than it pays.",
        ],
      },
      { t: "h2", x: "Why brands are paying attention" },
      {
        t: "p",
        x: "Brands want to be part of the fastest-growing court sport in front of engaged audiences, and athletes are their entry point. The players who land the best partnerships are the ones who can show an audience, a content rhythm and a professional team behind them.",
      },
      { t: "h2", x: "The agency's role in all of this" },
      {
        t: "p",
        x: "An agency coordinates the stack — brand development, media strategy, partnerships and career planning under one plan. That's the difference between a player with moments and a player with a career, and it's the same reason athletes in older professional sports surrounded themselves with a team early.",
      },
      {
        t: "p",
        x: "SMG Sports Management Group International builds athlete brands with the full resources of SMG Media Group — media strategy, content development and brand partnerships under one roof. Players can apply below; brands can meet the roster on the home page.",
      },
    ],
  },
  {
    slug: "pickleball-sponsorship-for-brands",
    seoTitle: "Pickleball Sponsorship for Brands | SMG Sports Management",
    title: "Pickleball Sponsorship: A Brand's Playbook",
    description:
      "How brands approach pickleball sponsorship: partnership types, what drives cost, how to pick the right athletes, and how an agency runs the deal.",
    date: "2026-10-15",
    minutes: 4,
    body: [
      {
        t: "p",
        x: "Pickleball sponsorship is how most brands enter the fastest-growing court sport — and the brands that do it well treat it as a partnership program, not a logo buy. This playbook covers the partnership types, what actually drives cost, and how to pick athletes who carry your brand properly.",
      },
      { t: "h2", x: "Why pickleball works for brands" },
      {
        t: "p",
        x: "Few sports combine this audience profile: players and fans across a wide age range, strong community participation, and an athlete market that is still early — far less saturated than golf or tennis. Brands get category attention and authentic ambassadors before the market crowds.",
      },
      { t: "h2", x: "The partnership menu" },
      {
        t: "ul",
        items: [
          "Athlete endorsements — appearances, social content and co-branded products with roster players.",
          "Event activations — clinics, demos and tournaments that put your product in players' hands.",
          "Regional ambassadors — emerging pros who anchor your brand in a specific market.",
          "Tour and league inventory — broader visibility across the professional scene.",
        ],
      },
      { t: "h2", x: "What actually drives cost" },
      {
        t: "p",
        x: "Budgets range widely, and the drivers matter more than any headline number: the athlete's tier and results, the rights included (content volume, exclusivity, image usage), the term length, and travel or appearance commitments. The right first question is not 'what does a sponsorship cost' but 'which audience and which rights do we actually need'.",
      },
      { t: "h2", x: "How to pick the right athlete" },
      {
        t: "ul",
        items: [
          "Audience fit beats follower counts — engaged, relevant communities convert.",
          "Content rhythm — athletes who already post consistently activate partnerships faster.",
          "A professional team behind them — agency-represented athletes are easier to contract with and coordinate.",
          "Character and media comfort — the partnership will be judged by every public appearance.",
        ],
      },
      { t: "h2", x: "Why work through an agency" },
      {
        t: "p",
        x: "An agency matches you with the right athletes, runs the contracting, coordinates activation and keeps the partnership aligned with your goals across the term. It replaces months of scouting and back-and-forth with a single point of accountability.",
      },
      {
        t: "p",
        x: "SMG Sports Management Group International builds brand partnerships across its roster from Carlsbad, California — endorsements, event partnerships, travel opportunities and co-branded products. Tell us your audience and goals through the brands form below, and we'll match you with the right athletes.",
      },
    ],
  },
  {
    slug: "when-to-get-a-pickleball-agent",
    seoTitle: "When Should You Get a Pickleball Agent? | SMG Sports Management",
    title: "When Should You Get a Pickleball Agent? Six Signs You're Ready",
    description:
      "Six signs you're ready for a pickleball agent — and what to do while you build your case. Straight answers from SMG Sports Management, a pickleball representation agency.",
    date: "2026-10-27",
    minutes: 4,
    body: [
      {
        t: "p",
        x: "Every player who climbs the ranks hits the same question: should I get an agent yet? Ask too early and you might hand over a slice of deals you could have made alone. Ask too late and you've already signed away value you didn't know you had. Here's an honest framework — the same one we use when players approach SMG.",
      },
      { t: "h2", x: "The honest answer" },
      {
        t: "p",
        x: "There's no magic ranking that makes representation 'worth it'. The right moment is when the business around your playing starts to compete with your playing. If offers, media requests and contract questions are stacking up faster than you can answer them well, that's the signal — not a specific DUPR or tour card.",
      },
      { t: "h2", x: "Six signs you're ready" },
      {
        t: "ul",
        items: [
          "You're placing at your level and moving up a tour — trajectory matters more than your current tier.",
          "Brands, tournaments or media have started contacting you directly, and you're not sure which opportunities are real.",
          "You've been offered something in writing and genuinely don't know if the terms are good.",
          "You're underpricing or under-packaging your content and results because you have no benchmark.",
          "Business admin is eating into training blocks — the compounding cost nobody sees.",
          "You're starting to think past playing: commentary, owning a business, coaching, licensing your name.",
        ],
      },
      { t: "h2", x: "Why earlier can beat later" },
      {
        t: "p",
        x: "Representation isn't only for players already cashing checks. Agencies invest in trajectory: a player with results, a growing audience and a clean professional foundation is exactly who agencies build multi-year value around. The earlier the system is in place, the more every future result is worth.",
      },
      { t: "h2", x: "When you honestly don't need one yet" },
      {
        t: "p",
        x: "If you're still building your results record, nobody is contacting you yet, and the deals you see are small and simple — handle them yourself, learn the landscape, and keep everything professional. A reputable agency will tell you this too. What you do in this phase (results, content, a proper media kit) determines what representation can do for you later.",
      },
      { t: "h2", x: "What to do in the meantime" },
      {
        t: "ul",
        items: [
          "Keep a clean results record across sanctioned events — agencies verify everything.",
          "Build a media kit: bio, highlights, rankings, audience numbers, contact.",
          "Post consistently; brands check your feeds before they check anything else.",
          "Apply anyway — agencies review emerging players on trajectory, not just results.",
        ],
      },
      {
        t: "p",
        x: "SMG Sports Management Group International represents professionals, emerging pros and youth and NIL talent from Carlsbad, California. If two or more of the six signs sound like you, the athlete application below takes a few minutes — and either way, you'll know where you stand.",
      },
    ],
  },
  {
    slug: "pickleball-agent-cost",
    seoTitle: "How Much Does a Pickleball Agent Cost? | SMG Sports Management",
    title: "How Much Does a Pickleball Agent Cost? Fees, Models and Red Flags",
    description:
      "How pickleball agencies charge: commission, flat and hybrid models, what you should never pay, and the questions to ask before signing — from SMG Sports Management.",
    date: "2026-10-27",
    minutes: 4,
    body: [
      {
        t: "p",
        x: "It's the question players google at midnight: what does representation actually cost? The honest answer is that legitimate agencies rarely have a fixed price tag — they're paid from the value they generate, in a few standard structures. Here's how the models work and how to spot the arrangements that should worry you.",
      },
      { t: "h2", x: "The three standard models" },
      {
        t: "ul",
        items: [
          "Commission — the agency earns a percentage of the deals it negotiates or manages for you. No deals, no income for the agency. This aligns incentives: the agency only eats when you eat.",
          "Flat fee or retainer — a fixed amount for defined work, like a brand audit, a media kit and outreach plan, or negotiating one specific contract.",
          "Hybrid — a smaller retainer plus a reduced commission, common when a player wants guaranteed attention while the deal pipeline is being built.",
        ],
      },
      { t: "h2", x: "What you should never pay" },
      {
        t: "p",
        x: "Be very wary of anyone asking for a large upfront 'signing fee' just to become a client, or guaranteed placement offers priced before anyone has seen your results. Representation is a professional relationship, not a pay-to-join club. The standard of the industry is that the agency's income comes from the deals it wins for you — if the pitch inverts that, ask why.",
      },
      { t: "h2", x: "What the fee should buy you" },
      {
        t: "p",
        x: "A good agency should more than pay for itself — through deals you'd never have sourced alone, terms you'd have undersold, and a brand that compounds instead of leaking value. When you evaluate cost, weigh it against the full system: sponsorship pipeline, negotiation, brand development, media handling and long-term career planning. A cheap agent who closes nothing is the most expensive option on the table.",
      },
      { t: "h2", x: "Questions to ask before you sign anything" },
      {
        t: "ul",
        items: [
          "Exactly which services are included — and which are billed separately?",
          "Who negotiates my deals day to day, and what have they closed in pickleball?",
          "How does commission apply across sponsorships, appearances and content deals?",
          "What's the term length, and how hard is it to leave if it isn't working?",
          "Which brands and tours does the agency already work with?",
        ],
      },
      {
        t: "p",
        x: "SMG Sports Management Group International represents professionals, emerging players and youth and NIL talent from Carlsbad, California — backed by two decades of representation experience across major sports. If you'd like to talk about what representation would look like for you, the athlete application below is the fastest way into the room.",
      },
    ],
  },
];
